import { uploadChatAttachments } from "controller";
import { useSetAtom } from "jotai";
import { useCallback, useRef } from "react";
import type { AttachmentFileData } from "shared/types";
import { v4 as uuidv4 } from "uuid";
import { addChatFileAttachments, updateChatFileAttachment } from "../store";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file

interface UseFileAttachmentUploadParams {
  serviceId: string;
  serviceType: string;
  isDisabled: boolean;
}

export const useFileAttachmentUpload = ({
  serviceId,
  serviceType,
  isDisabled,
}: UseFileAttachmentUploadParams) => {
  const addFiles = useSetAtom(addChatFileAttachments);
  const updateFile = useSetAtom(updateChatFileAttachment);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadSingleFile = useCallback(
    (file: File, key: string) => {
      const formData = new FormData();
      formData.append("files", file);

      uploadChatAttachments({
        formData,
        serviceId,
        serviceType,
      }).subscribe({
        next: ({
          response,
          status,
          percentage,
          controller,
        }: {
          response: Record<string, unknown>;
          status: string;
          percentage: number;
          controller: AbortController;
        }) => {
          if (status === "COMPLETE") {
            if (
              (response as Record<string, unknown>)?.success &&
              (response as Record<string, unknown>)?.statusCode === 200
            ) {
              updateFile({
                key,
                updates: {
                  status: "Uploaded",
                  progress: 100,
                  responseData: (
                    response as { data: AttachmentFileData["responseData"][] }
                  ).data[0],
                },
              });
            } else {
              updateFile({
                key,
                updates: { status: "Error", progress: 0 },
              });
            }
          } else {
            updateFile({
              key,
              updates: {
                status: "Uploading",
                progress: percentage,
                controller,
              },
            });
          }
        },
        error: () => {
          updateFile({
            key,
            updates: { status: "Error", progress: 0 },
          });
        },
      });
    },
    [serviceId, serviceType, updateFile],
  );

  const handleFilesSelected = useCallback(
    (files: FileList | File[]) => {
      if (isDisabled) return;

      const fileArray = Array.from(files);
      const newFileData: AttachmentFileData[] = fileArray.map((file) => {
        const exceedsLimit = file.size > MAX_FILE_SIZE;
        return {
          key: uuidv4(),
          name: file.name,
          size: String(file.size),
          type: file.type,
          status: exceedsLimit ? ("Error" as const) : ("Uploading" as const),
          progress: 0,
          file,
          responseData: {} as AttachmentFileData["responseData"],
          ...(exceedsLimit && {
            errorMessage: `File exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(1)}MB)`,
          }),
        };
      });

      addFiles(newFileData);

      newFileData.forEach((fd) => {
        if (fd.status !== "Error") {
          uploadSingleFile(fd.file, fd.key);
        }
      });
    },
    [isDisabled, addFiles, uploadSingleFile],
  );

  const openFilePicker = useCallback(() => {
    if (isDisabled) return;
    fileInputRef.current?.click();
  }, [isDisabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFilesSelected(files);
      }
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handleFilesSelected],
  );

  return {
    fileInputRef,
    openFilePicker,
    handleInputChange,
  };
};
